// useDiagramPipeline — React-hook som exponerar agent-pipeline-execution
// som reactive state. Wrappar runPipeline + createLlmAgentRunner (X1) + en
// injicerbar LlmCall. Konsumerande komponent (AiAssistantDialog) får:
//   - state: per-agent-status som matchar AiAgentStepper.AgentStepState
//   - start(userPrompt): kickar igång pipeline:n
//   - cancel(): abort-signal till pågående LLM-call
//   - totalCost / finalReport efter avslut
//   - builtWorkspace: det deterministiskt byggda MultiSheetWorkflowState-t
//     (Y2) som user kan importera till canvas via onImport
//
// Hooken är LLM-agnostisk — caller injicerar llmCall (mock i tests,
// edge-function-wrapper i produktion). Y1: caller kan även injicera
// credit-gating-fns så pipeline:n reservas/commit:as/release:as
// atomärt runt körningen.

import { useCallback, useRef, useState } from 'react';
import {
  runPipeline,
  DIAGRAM_PIPELINE,
  getAgent,
  type PipelineReport,
} from '@/lib/aiAgents/agentRegistry';
import {
  createLlmAgentRunner,
  type LlmCall,
} from '@/lib/aiAgents/llmAgentRunner';
import type { AgentStepState } from '@/components/ai/AiAgentStepper';
import type { MultiSheetWorkflowState } from '@/types/workflow';
import { planDiagramFromPrompt } from '@/lib/aiSkills/diagramFromPrompt';
import { planDiagramFromSupabaseSchema, type DbSchema } from '@/lib/aiSkills/diagramFromSupabaseSchema';
import { planDiagramFromCodebase } from '@/lib/aiSkills/diagramFromCodebase';
import { analyzeFileTree, type FileNode } from '@/lib/aiSkills/codebaseAnalyzer';
import { runPlan } from '@/lib/aiSkills/dispatcher';
import { buildDefaultToolImpls } from '@/lib/aiSkills/defaultToolImpls';
import { autoLayout, refactorLayoutNoOverlap, detectOverlaps } from '@/lib/diagramLayout';

// AA2e: intent-detektion för val av skill. Samma regex som
// diagramFromSupabaseSchema.trigger / diagramFromCodebase.trigger
// men duplicerade här för lazy-selection utan side-effect-importer.
const SCHEMA_INTENT = /\b(?:ERD|entity(?:-| )relation(?:ship)?)\b|schema(?:-| )?diagram|databas(?:-| )?(?:schema|karta)|rita\s+(?:db|databasen|schemat)/i;
const CODEBASE_INTENT = /modul(?:-| )?diagram|(?:module|modul)(?:-| )graph|kartl[aä]gg(?:\s+src)?|rita\s+(?:koden|kodbasen)/i;

function detectSkill(prompt: string): 'schema' | 'codebase' | 'prompt' {
  if (SCHEMA_INTENT.test(prompt)) return 'schema';
  if (CODEBASE_INTENT.test(prompt)) return 'codebase';
  return 'prompt';
}

export type DiagramPipelineStatus = 'idle' | 'running' | 'done' | 'failed' | 'cancelled';

// Y1: credit-gating-typer matchar useAiCreditGating-signaturen men är
// strukturellt duplicerade här så hooken inte introducerar cyklisk dep.
export interface CreditGatingHooks {
  preflightAndReserve: (input: { correlationId: string; estimatedAiCredits: number }) => Promise<{ ok: boolean; reason?: string }>;
  commitReservation: (input: { correlationId: string; actualCredits: number }) => Promise<{ ok: boolean; refunded?: number }>;
  releaseReservation: (input: { correlationId: string }) => Promise<{ ok: boolean; released?: number }>;
}

export interface UseDiagramPipelineInput {
  /** LlmCall-impl som kallas per agent. */
  llmCall: LlmCall;
  /** Workspace som pipeline:n operar mot (default: tomt multi-sheet). */
  initialWorkspace?: MultiSheetWorkflowState;
  language?: 'sv' | 'en';
  /** Y1: credit-gating wrappas runt start() — preflight före, commit/release efter. */
  creditGating?: CreditGatingHooks;
  /** Y1: estimerad totalkostnad (från estimatePipelineCost) — skickas till preflight. */
  estimatedCredits?: number;
  /**
   * AB1 + AA2e: admin-flag för kod-/schema-läsande skills. Om false
   * (default) och user triggar diagramFromCodebase eller ERD-intent
   * → buildWorkspace fall:er tillbaka till diagramFromPrompt.
   */
  isAdmin?: boolean;
  /**
   * AA2e: async-loaders som hookens build-step anropar när user:s prompt
   * matchar respektive intent. Båda admin-gate:ade: loaders:na får
   * aldrig köras om isAdmin=false.
   */
  loadSchema?: () => Promise<{ ok: boolean; schema?: DbSchema; error?: string; message?: string }>;
  loadCodebaseFiles?: () => Promise<FileNode[]>;
}

export interface UseDiagramPipelineResult {
  status: DiagramPipelineStatus;
  steps: AgentStepState[];
  totalCost: number;
  report: PipelineReport | null;
  /** Y2: det byggda multi-sheet-workspacet redo för onImport. Null innan done. */
  builtWorkspace: MultiSheetWorkflowState | null;
  start: (userPrompt: string) => Promise<PipelineReport>;
  cancel: () => void;
  reset: () => void;
}

const EMPTY_WORKSPACE: MultiSheetWorkflowState = {
  name: 'ai-diagram',
  sheets: [],
  crossSheetConnectors: [],
  activeSheetIndex: 0,
};

function initialSteps(): AgentStepState[] {
  return DIAGRAM_PIPELINE.map((s) => ({
    agentId: s.agentId,
    label: getAgent(s.agentId)?.description.split('.')[0] ?? s.agentId,
    status: 'pending',
  }));
}

function makeCorrelationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `diagram-${crypto.randomUUID()}`;
  return `diagram-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Y2: Bygg deterministiskt multi-sheet-workspace från user-prompt via
 * client-side plan-exekvering. Används efter pipeline-godkännande så user
 * får en förutsägbar struktur att importera — LLM-output styr formulering
 * och plan, men själva bygget sker lokalt via buildDefaultToolImpls.
 *
 * Z3: Efter bygget körs auto-layout per sheet så blocken ligger i en
 * läsbar grid istället för rå plan-pos. Om något sheet har overlaps efter
 * default-grid → kör refactorLayoutNoOverlap som spread:ar tills clean.
 */
interface BuildOpts {
  isAdmin?: boolean;
  loadSchema?: UseDiagramPipelineInput['loadSchema'];
  loadCodebaseFiles?: UseDiagramPipelineInput['loadCodebaseFiles'];
}

async function buildWorkspaceFromPrompt(
  userPrompt: string,
  opts: BuildOpts = {},
): Promise<MultiSheetWorkflowState> {
  const kind = detectSkill(userPrompt);
  // AA2e: admin-intent branches. Non-admin fallbacks till diagramFromPrompt
  // så kod-/DB-data aldrig läcks via denna path. planDiagramFromCodebase
  // + planDiagramFromSupabaseSchema har egna defense-in-depth-guards om
  // någon skulle råka kalla dem utan isAdmin.
  let plan;
  if (kind === 'schema' && opts.isAdmin && opts.loadSchema) {
    const loaded = await opts.loadSchema();
    if (loaded.ok && loaded.schema) {
      plan = planDiagramFromSupabaseSchema({
        userPrompt, isAdmin: true, schema: loaded.schema,
      });
    } else {
      // Loader failed → fallback till prompt-baserad arkitektur.
      console.warn('[useDiagramPipeline] schema-loader misslyckades', loaded.error, loaded.message);
      plan = planDiagramFromPrompt({ userPrompt });
    }
  } else if (kind === 'codebase' && opts.isAdmin && opts.loadCodebaseFiles) {
    const files = await opts.loadCodebaseFiles();
    const moduleGraph = analyzeFileTree(files);
    plan = planDiagramFromCodebase({
      userPrompt, isAdmin: true, moduleGraph,
    });
  } else {
    plan = planDiagramFromPrompt({ userPrompt });
  }

  const workspace = {
    current: {
      name: 'AI diagram',
      sheets: [],
      crossSheetConnectors: [],
      activeSheetIndex: 0,
    } as MultiSheetWorkflowState,
  };
  const tools = buildDefaultToolImpls(workspace);
  runPlan(plan, { tools });
  // Z3: Per-sheet auto-layout med overlap-detection.
  workspace.current.sheets = workspace.current.sheets.map((sheet) => {
    const laidOut = autoLayout(sheet.blocks);
    const overlaps = detectOverlaps(laidOut);
    return {
      ...sheet,
      blocks: overlaps.length > 0 ? refactorLayoutNoOverlap(sheet.blocks) : laidOut,
    };
  });
  return workspace.current;
}

export function useDiagramPipeline(
  input: UseDiagramPipelineInput,
): UseDiagramPipelineResult {
  const [status, setStatus] = useState<DiagramPipelineStatus>('idle');
  const [steps, setSteps] = useState<AgentStepState[]>(initialSteps);
  const [report, setReport] = useState<PipelineReport | null>(null);
  const [totalCost, setTotalCost] = useState(0);
  const [builtWorkspace, setBuiltWorkspace] = useState<MultiSheetWorkflowState | null>(null);
  const cancelRef = useRef(false);
  const inputRef = useRef(input);
  inputRef.current = input;

  const reset = useCallback(() => {
    cancelRef.current = false;
    setStatus('idle');
    setSteps(initialSteps());
    setReport(null);
    setTotalCost(0);
    setBuiltWorkspace(null);
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current = true;
    setStatus('cancelled');
  }, []);

  const start = useCallback(
    async (userPrompt: string): Promise<PipelineReport> => {
      cancelRef.current = false;
      setStatus('running');
      setSteps(initialSteps());
      setReport(null);
      setTotalCost(0);
      setBuiltWorkspace(null);

      const correlationId = makeCorrelationId();
      const gating = inputRef.current.creditGating;
      const estimate = inputRef.current.estimatedCredits ?? 0;

      // Y1: preflight — avbryt innan LLM-call om insufficient balance.
      if (gating && estimate > 0) {
        const pre = await gating.preflightAndReserve({ correlationId, estimatedAiCredits: estimate });
        if (!pre.ok) {
          const failureReport: PipelineReport = {
            pipeline: DIAGRAM_PIPELINE,
            outputs: [],
            ok: false,
            firstFailure: {
              agent: 'planner', ok: false, payload: null, cost: 0, toolCalls: [],
              error: `credit-preflight-failed: ${pre.reason ?? 'unknown'}`,
            },
            totalCost: 0,
            finalPlan: null,
          };
          setReport(failureReport);
          setStatus('failed');
          setSteps((prev) => prev.map((s) => ({ ...s, status: 'skipped' })));
          return failureReport;
        }
      }

      const baseRunner = createLlmAgentRunner(inputRef.current.llmCall);
      const trackingRunner = async (agent: Parameters<typeof baseRunner>[0], agentInput: Parameters<typeof baseRunner>[1]) => {
        if (cancelRef.current) {
          return {
            agent: agent.id, ok: false, payload: null, cost: 0, toolCalls: [],
            error: 'cancelled',
          };
        }
        setSteps((prev) => prev.map((s) => s.agentId === agent.id ? { ...s, status: 'running' } : s));
        const out = await baseRunner(agent, agentInput);
        setSteps((prev) => prev.map((s) => {
          if (s.agentId !== agent.id) return s;
          return {
            ...s,
            status: out.ok ? 'ok' : 'failed',
            cost: out.cost,
            error: out.error,
            summary: summariseOutput(out.payload),
          };
        }));
        setTotalCost((prev) => prev + out.cost);
        return out;
      };

      const workspace = inputRef.current.initialWorkspace ?? EMPTY_WORKSPACE;
      const sharedContext = {
        userPrompt,
        language: inputRef.current.language ?? 'sv' as const,
      };
      const result = await runPipeline(
        DIAGRAM_PIPELINE,
        sharedContext,
        workspace,
        trackingRunner,
      );
      const finalStatus: DiagramPipelineStatus = cancelRef.current
        ? 'cancelled'
        : (result.ok ? 'done' : 'failed');
      setReport(result);
      setStatus(finalStatus);
      setSteps((prev) => prev.map((s) => s.status === 'pending' ? { ...s, status: 'skipped' } : s));

      // Y1: credit-finalisering. Commit vid done, release vid fail/cancel.
      if (gating && estimate > 0) {
        const actual = result.outputs.reduce((sum, o) => sum + o.cost, 0);
        if (finalStatus === 'done') {
          await gating.commitReservation({ correlationId, actualCredits: actual });
        } else {
          await gating.releaseReservation({ correlationId });
        }
      }

      // Y2: bygg konkret workspace om pipeline:n lyckades. Client-side
      // exekvering av plan:en → deterministisk struktur som user kan
      // importera via onImport-callback.
      // AA2e: async eftersom loader:s (schema / codebase) kan ringa RPC.
      if (finalStatus === 'done') {
        try {
          const ws = await buildWorkspaceFromPrompt(userPrompt, {
            isAdmin: inputRef.current.isAdmin,
            loadSchema: inputRef.current.loadSchema,
            loadCodebaseFiles: inputRef.current.loadCodebaseFiles,
          });
          setBuiltWorkspace(ws);
        } catch (err) {
          console.warn('[useDiagramPipeline] build-workspace failed', err);
        }
      }

      return result;
    },
    [],
  );

  return { status, steps, totalCost, report, builtWorkspace, start, cancel, reset };
}

function summariseOutput(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const p = payload as Record<string, unknown>;
  if (typeof p.score === 'number') return `score ${p.score}`;
  if (typeof p.sheetCount === 'number') return `${p.sheetCount} sheets`;
  if (p.plan && typeof p.plan === 'object') {
    const plan = p.plan as { steps?: unknown[] };
    return `${plan.steps?.length ?? 0} steg`;
  }
  if (typeof p.approved === 'boolean') return p.approved ? 'approved' : 'revise';
  return undefined;
}
